import { Block } from 'shared-types';

type Properties = { dynamic: RegExp[]; static: string[] };

// overloads for better inference and compatibility with callers using either `type` or `blockType`
function getBlockTranslatableProperties(params: {
  type?: never;
}): Record<Block['type'], Properties>;
function getBlockTranslatableProperties(params: {
  type: Block['type'];
}): Properties;

// implementation
function getBlockTranslatableProperties(params?: {
  type?: Block['type'];
}): Record<Block['type'], Properties> | Properties {
  const propertiesByType: Record<Block['type'], Properties> = {
    audio: {
      dynamic: [],
      static: []
    },
    discussion: {
      dynamic: [],
      static: ['text', 'title']
    },
    embedcode: {
      dynamic: [],
      static: ['openFullScreenMessage']
    },
    embedlink: {
      dynamic: [],
      static: ['openFullScreenMessage']
    },
    file: {
      dynamic: [],
      static: ['title']
    },
    fillin: {
      // 'content data and correct answers'
      dynamic: [/^(correctAnswers)_\d+_\d+$/],
      static: [
        'question',
        'content',
        'feedbackCorrect',
        'feedbackIncorrect',
        'feedbackPartiallyCorrect'
      ]
    },
    h5p: {
      dynamic: [],
      static: ['openFullScreenMessage']
    },
    heading: {
      dynamic: [],
      static: ['text']
    },
    image: {
      dynamic: [],
      static: ['altText', 'caption']
    },
    likert: {
      // options and questions
      dynamic: [/^(option|question)_\d+$/],
      static: ['description', 'feedback']
    },
    list: {
      dynamic: [],
      static: ['listHtml']
    },
    mathjax: {
      dynamic: [],
      static: ['text']
    },
    multiplechoice: {
      // option text and feedback
      dynamic: [/^(option|question)_\d+_(text|feedback)$/],
      static: [
        'question',
        'feedback',
        'feedbackCorrect',
        'feedbackIncorrect',
        'feedbackPartiallyCorrect'
      ]
    },
    numericquestion: {
      dynamic: [],
      static: ['question', 'feedback', 'feedbackCorrect', 'feedbackIncorrect']
    },
    openquestion: {
      dynamic: [],
      static: ['question', 'feedback']
    },
    pagebreak: {
      dynamic: [],
      static: []
    },
    pre: {
      dynamic: [],
      static: ['text']
    },
    quote: {
      dynamic: [],
      static: ['text', 'attribution']
    },
    raw: {
      dynamic: [],
      static: ['text']
    },
    scorm: {
      dynamic: [],
      static: ['openFullScreenMessage']
    },
    sequencing: {
      // option text
      dynamic: [/^(option)_\d+_text$/],
      static: [
        'question',
        'feedbackCorrect',
        'feedbackIncorrect',
        'feedbackPartiallyCorrect'
      ]
    },
    table: {
      dynamic: [],
      static: ['text']
    },
    text: {
      dynamic: [],
      static: ['text']
    },
    video: {
      // video links and sublinks
      dynamic: [/^link_\d+_(title)$|^link_\d+_sublink_\d+_(title)$/],
      static: []
    }
  };
  const type = params?.type;
  if (!type) return propertiesByType;

  const properties = propertiesByType[type];
  if (!properties) {
    throw new Error(
      `Block type ${type} not supported in getBlockTranslatableProperties!`
    );
  }

  return properties;
}

export default getBlockTranslatableProperties;
